import React, { useState } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Tag, 
  Progress, 
  Space, 
  Button,
  Collapse,
  Empty,
  Spin,
} from 'antd';
import { 
  ReloadOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  LoadingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { settingsApi, DomainCheckJob } from '../services/settingsApi';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

const WebhookStatus: React.FC = () => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [errorsPagination, setErrorsPagination] = useState<Record<string, { page: number; limit: number }>>({});

  const { data: jobs, isLoading, refetch } = useQuery(
    ['domainCheckJobs'],
    () => settingsApi.getAllDomainCheckJobs(), // Get ALL jobs (no limit) to show complete history
    {
      refetchInterval: 5000, // Auto-refresh every 5 seconds for real-time updates
      staleTime: 0, // Always consider data stale to ensure fresh data
      cacheTime: 0, // Don't cache to always fetch fresh data
    }
  );

  const getStatusTag = (status: DomainCheckJob['status']) => {
    const statusConfig = {
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Pending' },
      processing: { color: 'processing', icon: <LoadingOutlined spin />, text: 'Processing' },
      completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' },
      stopped: { color: 'warning', icon: <StopOutlined />, text: 'Stopped' },
    };
    const config = statusConfig[status];
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getProgress = (job: DomainCheckJob) => {
    if (job.totalDomains === 0) return 0;
    return Math.round((job.completedDomains / job.totalDomains) * 100);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const columns: ColumnsType<DomainCheckJob> = [
    {
      title: 'Job ID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: 200,
      render: (jobId: string) => (
        <Text code style={{ fontSize: '12px' }}>{jobId}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: DomainCheckJob['status']) => getStatusTag(status),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 200,
      render: (_: any, record: DomainCheckJob) => (
        <div>
          <Progress 
            percent={getProgress(record)} 
            status={record.status === 'failed' ? 'exception' : 'active'}
            size="small"
            format={() => `${record.completedDomains}/${record.totalDomains}`}
          />
        </div>
      ),
    },
    {
      title: 'Completed',
      dataIndex: 'completedDomains',
      key: 'completedDomains',
      width: 100,
      align: 'center' as const,
      render: (completed: number) => (
        <Tag color="green">{completed}</Tag>
      ),
    },
    {
      title: 'Failed',
      dataIndex: 'failedDomains',
      key: 'failedDomains',
      width: 100,
      align: 'center' as const,
      render: (failed: number) => (
        <Tag color="red">{failed}</Tag>
      ),
    },
    {
      title: 'Started At',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Completed At',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Log File',
      key: 'logFilePath',
      width: 200,
      render: (_: any, record: DomainCheckJob) => {
        if (!record.logFilePath) {
          return <Text type="secondary">N/A</Text>;
        }
        return (
          <Text code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {record.logFilePath}
          </Text>
        );
      },
    },
  ];

  const ErrorsTable: React.FC<{ jobId: string; totalErrors: number }> = ({ jobId, totalErrors }) => {
    const pagination = errorsPagination[jobId] || { page: 1, limit: 20 };
    
    const { data: errorsData, isLoading: isLoadingErrors } = useQuery(
      ['jobErrors', jobId, pagination.page, pagination.limit],
      () => settingsApi.getJobErrors(jobId, pagination.page, pagination.limit),
      {
        enabled: expandedRowKeys.includes(jobId),
        staleTime: 30000, // Cache for 30 seconds
      }
    );

    const handleTableChange = (page: number, pageSize: number) => {
      setErrorsPagination(prev => ({
        ...prev,
        [jobId]: { page, limit: pageSize },
      }));
    };

    if (isLoadingErrors) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }

    if (!errorsData || errorsData.errors.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Empty description="No errors" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <Table
        dataSource={errorsData.errors}
        rowKey={(error, index) => `${error.domain}-${index}`}
        pagination={{
          current: errorsData.page,
          pageSize: errorsData.limit,
          total: errorsData.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} errors`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
        }}
        size="small"
        columns={[
          {
            title: 'Domain',
            dataIndex: 'domain',
            key: 'domain',
            width: 250,
            render: (domain: string) => <Text code>{domain}</Text>,
          },
          {
            title: 'Error',
            dataIndex: 'error',
            key: 'error',
            render: (error: string) => (
              <Text type="danger">{error}</Text>
            ),
          },
          {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp: string) => formatDate(timestamp),
          },
        ]}
      />
    );
  };

  const expandedRowRender = (record: DomainCheckJob) => {
    if (record.failedDomains === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Empty description="No errors" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <div style={{ padding: '16px', background: '#fafafa' }}>
        <Title level={5} style={{ marginBottom: '16px' }}>
          Errors ({record.failedDomains})
        </Title>
        <ErrorsTable jobId={record.jobId} totalErrors={record.failedDomains} />
      </div>
    );
  };

  return (
    <div>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>Webhook Status</Title>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Space>

        {isLoading && !jobs ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : !jobs || jobs.length === 0 ? (
          <Empty description="No domain check jobs found" />
        ) : (
          <Table
            dataSource={jobs}
            rowKey="jobId"
            columns={columns}
            loading={isLoading}
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => {
                      setExpandedRowKeys(keys as string[]);
                      // Reset pagination for newly expanded rows
                      const newKeys = keys as string[];
                      const oldKeys = expandedRowKeys;
                      const newlyExpanded = newKeys.filter(key => !oldKeys.includes(key));
                      newlyExpanded.forEach(jobId => {
                        setErrorsPagination(prev => ({
                          ...prev,
                          [jobId]: { page: 1, limit: 20 },
                        }));
                      });
                    },
                  }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} jobs`,
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>
    </div>
  );
};

export default WebhookStatus;

